import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface DialogRef {
  open: () => void;
  close: () => void;
}

interface GenericDialogProps {
  title?: string;
  subtitle?: string;
  loading?: boolean;
  onConfirm?: () => void;
  children: React.ReactNode;
  headerChildren?: React.ReactNode;
  onClose?: () => void;
  width?: Breakpoint;
  /** Confirm button label (translation key or plain text) */
  confirmText?: string;
  /** Cancel button label (translation key or plain text) */
  cancelText?: string;
  confirmDisabled?: boolean;
  confirmColor?: "primary" | "error" | "success" | "warning" | "inherit";
  confirmIcon?: React.ReactNode;
  /** Hide the whole footer */
  hideActions?: boolean;
  /** Hide the cancel button */
  hideCancel?: boolean;
  /** Custom footer content, rendered before the buttons (left side) */
  footerChildren?: React.ReactNode;
  /** Close the dialog after confirm (default: false) */
  closeOnConfirm?: boolean;
  fullScreenOnMobile?: boolean;
  /** Remove default padding of the content area */
  disableContentPadding?: boolean;
}

export const GenericDialog = forwardRef<DialogRef, GenericDialogProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const { onClose } = props;

    const open = useCallback(() => setIsOpen(true), []);

    const close = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    useImperativeHandle(ref, () => ({ open, close }), [open, close]);

    const handleConfirm = () => {
      props.onConfirm?.();
      if (props.closeOnConfirm) close();
    };

    const showActions = !props.hideActions;

    return (
      <Dialog
        open={isOpen}
        onClose={close}
        maxWidth={props.width ?? "sm"}
        fullWidth
        fullScreen={false}
        keepMounted={false}
        PaperProps={{
          sx: {
            bgcolor: "#252a32",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            m: { xs: 1.5, sm: 4 },
            maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(255,255,255,0.02)",
          }}
        >
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
              <Typography
                variant="h6"
                component="h2"
                color="text.primary"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.05rem", sm: "1.2rem" },
                  lineHeight: 1.3,
                }}
              >
                {t(props.title ?? "Details")}
              </Typography>

              {props.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {t(props.subtitle)}
                </Typography>
              )}
              {props.headerChildren}
            </Box>
            <IconButton
              onClick={close}
              size="small"
              aria-label="close"
              sx={{
                alignSelf: "flex-start",
                color: "text.secondary",
                "&:hover": { color: "text.primary" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: props.disableContentPadding ? 0 : 3,
            "&&": { pt: props.disableContentPadding ? 0 : 3 },
          }}
        >
          {isOpen && props.children}
        </DialogContent>

        {showActions && (
          <DialogActions
            sx={{
              px: 3,
              py: 1.5,
              gap: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "rgba(255,255,255,0.02)",
              justifyContent: props.footerChildren
                ? "space-between"
                : "flex-end",
            }}
          >
            {props.footerChildren && <Box>{props.footerChildren}</Box>}
            <Box display="flex" gap={1}>
              {!props.hideCancel && (
                <Button onClick={close} color="inherit">
                  {t(props.cancelText ?? "tooltip:cancel")}
                </Button>
              )}
              {props.onConfirm && (
                <LoadingButton
                  loading={props.loading}
                  disabled={props.confirmDisabled}
                  color={props.confirmColor ?? "primary"}
                  startIcon={props.confirmIcon}
                  variant="contained"
                  onClick={handleConfirm}
                >
                  {t(props.confirmText ?? "shareVetModale:confirm")}
                </LoadingButton>
              )}
            </Box>
          </DialogActions>
        )}
      </Dialog>
    );
  },
);